import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box,
    Button,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Stack,
    useMediaQuery,
    useTheme,
    IconButton,
    AppBar,
    Toolbar,
    Avatar,
    Chip,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
} from '@mui/material';
import {
    PlayArrow,
    People,
    Language,
    Event,
    Person,
    Security,
    Headset,
    KeyboardArrowDown,
    CalendarToday,
    Menu as MenuIcon,
    Login as LoginIcon,
    PersonAdd,
    Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo_cinza from '../assets/logo_branca.png'
import Portal from '@mui/material/Portal';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { useFetch } from '../hooks/useFetch';
import { StyledSwiper, SwiperFadeWrapper, SwiperNavContainer, NavButton } from './FindMeetings/Style';
import MeetingCard from '../components/MeetingCard/Index';
import { useServerTime } from '../hooks/useServerTime';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// -----------------------------
// Appear-on-scroll primitives
// -----------------------------

function useInView({ root = null, rootMargin = '0px', threshold = 0.15, once = true } = {}) {
    const [inView, setInView] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (prefersReduced) {
            setInView(true);
            return;
        }

        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setInView(true);
                        if (once) obs.unobserve(entry.target);
                    } else if (!once) {
                        setInView(false);
                    }
                });
            },
            { root, rootMargin, threshold }
        );

        obs.observe(el);
        return () => obs.disconnect();
    }, [root, rootMargin, threshold, once]);

    return { ref, inView };
}

const Reveal = ({
    as = 'div',
    children,
    delay = 0,
    duration = 1200,
    distance = 22,
    threshold = 0.15,
    once = true,
    fullHeight = false,   // NEW: keeps card height equal across rows
    sx = {},
    ...props
}) => {
    const { ref, inView } = useInView({ threshold, once });
    const Comp = useMemo(() => Box, []);

    return (
        <Comp
            component={as}
            ref={ref}
            sx={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'none' : `translateY(${distance}px)`,
                transition: `opacity ${duration}ms ease, transform ${duration}ms ease`,
                transitionDelay: `${delay}ms`,
                willChange: 'opacity, transform',
                backfaceVisibility: 'hidden',
                ...(fullHeight && {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }),
                ...sx,
            }}
            {...props}
        >
            {children}
        </Comp>
    );
};

// -----------------------------
// Existing navbar (unchanged)
// -----------------------------

const REVEAL_HOTSPOT_HEIGHT = 12;

function useHideOnScroll(threshold = 120) {
    const [visible, setVisible] = useState(true);
    const lastY = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
    const ticking = useRef(false);

    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY;
            const delta = y - lastY.current;

            if (ticking.current) return;
            ticking.current = true;

            window.requestAnimationFrame(() => {
                if (y < threshold) {
                    setVisible(true);
                } else {
                    if (delta > 0) setVisible(false);
                    else if (delta < 0) setVisible(true);
                }
                lastY.current = y;
                ticking.current = false;
            });
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [threshold]);

    return { visible, setVisible };
}

export const LandingNavbar = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('md'));
    const { visible, setVisible } = useHideOnScroll(120);
    const [drawerOpen, setDrawerOpen] = useState(false);

    return (
        <Portal container={document.body}>
            <Box
                onMouseEnter={() => setVisible(true)}
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: REVEAL_HOTSPOT_HEIGHT,
                    zIndex: (t) => t.zIndex.appBar + 3,
                    pointerEvents: 'auto',
                }}
            />

            <AppBar
                position="fixed"
                elevation={6}
                color="transparent"
                onMouseEnter={() => setVisible(true)}
                sx={{
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: (t) => t.zIndex.appBar + 2,
                    backgroundColor: '#006a61',
                    backdropFilter: 'blur(10px)',
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    transform: visible ? 'translateY(0)' : 'translateY(-110%)',
                    opacity: visible ? 1 : 0.9,
                    transition: 'transform 300ms ease, opacity 200ms ease',
                    pointerEvents: visible ? 'auto' : 'none',
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar
                        disableGutters
                        sx={{ minHeight: { xs: 64, md: 72 } }}
                        onFocusCapture={() => setVisible(true)}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={1}
                            sx={{ flexGrow: 1, cursor: 'pointer', lineHeight: 0 }}
                            onClick={() => navigate('/')}
                        >
                            <Box
                                component="img"
                                src={logo_cinza}
                                alt="Empiria"
                                sx={{
                                    width: 35,
                                    height: 35,
                                    display: 'block',
                                    objectFit: 'contain',
                                    m: 0,
                                    p: 0,
                                }}
                            />
                            <Typography variant="h6" sx={{ color: 'white', lineHeight: 1, m: 0 }}>
                                Empiria
                            </Typography>
                        </Stack>

                        {mobile ? (
                            <IconButton
                                onClick={() => setDrawerOpen(true)}
                                size="large"
                                edge="end"
                                color="inherit"
                                aria-label="menu">
                                <MenuIcon sx={{ color: 'white' }} />
                            </IconButton>
                        ) : (
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Button
                                    variant="text"
                                    onClick={() => navigate('/login')}
                                    sx={{ textTransform: 'none', fontWeight: 600, color: 'white' }}
                                >
                                    Login
                                </Button>
                                <Button
                                    variant="text"
                                    onClick={() => navigate('/criar-conta')}
                                    sx={{ textTransform: 'none', fontWeight: 600, color: 'white' }}
                                >
                                    Cadastro
                                </Button>
                            </Stack>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 280 }} role="presentation" onClick={() => setDrawerOpen(false)}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 2 }}>
                        <Box component="img" src={logo_cinza} sx={{ width: 30, height: 30, filter: 'brightness(0)' }} />
                        <Typography fontWeight={600} fontSize={18}>Empiria</Typography>
                    </Stack>
                    <Divider />
                    <List>
                        <ListItem disablePadding>
                            <ListItemButton onClick={() => navigate('/login')}>
                                <ListItemIcon><LoginIcon /></ListItemIcon>
                                <ListItemText primary="Login" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemButton onClick={() => navigate('/criar-conta')}>
                                <ListItemIcon><PersonAdd /></ListItemIcon>
                                <ListItemText primary="Cadastro" />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Box>
            </Drawer>
        </Portal>
    );
};

// -----------------------------
// Landing Page with animations
// -----------------------------

const Welcome = () => {
    // Sincroniza o relogio antes de os cards decidirem quais horarios ja passaram
    useServerTime();
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const videoRef = useRef(null);
    const anfitriaoRef = useRef(null);
    const convivaRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { logged, user } = useAuth();
    const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
    const { data } = useFetch("/auth/live-meetings");

    useEffect(() => {
        if (Boolean(logged)) {
            if (Boolean(user.profile)) navigate('/calendario');
            else navigate('/lista');
        }
    }, [logged, user, navigate]);

    // Auto-play video on mount
    useEffect(() => {
        if (videoRef.current) {
            // Tenta iniciar com som
            videoRef.current.muted = false;
            videoRef.current.play().catch(err => {
                console.log('Autoplay com som bloqueado, tentando com mute:', err);
                // Se falhar, tenta com mute
                if (videoRef.current) {
                    videoRef.current.muted = true;
                    videoRef.current.play().catch(error => {
                        console.log('Autoplay prevented:', error);
                    });
                }
            });
        }
    }, []);

    const scrollToAnfitriao = () => {
        anfitriaoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const scrollToConviva = () => {
        convivaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const encontros = [
        {
            id: 1,
            title: "Alinhamento das estrelas",
            host: "Carlos123",
            description: "De séculos em séculos as estrelas se alinham causando fenômenos incríveis que você não pode perder!",
            color: "#E9D5FF"
        },
        {
            id: 2,
            title: "Como nossos antepassados viam as estrelas",
            host: "TeacherAna",
            description: "As estrelas por muitos anos serviram como pontos de referência para os antigos navegadores. Venha descobrir como eles faziam isso.",
            color: "#FEF3C7"
        },
        {
            id: 3,
            title: "Aprenda a tocar Led Zeppelin em uma hora",
            host: "Carlos1961",
            description: "Led Zeppelin é maior banda de rock de todos os tempos. Vem comigo aprender a tocar alguns de seus maiores hits...",
            color: "#FECACA"
        }
    ];

    const testimonials = [
        {
            name: "Ana Carolina",
            text: "Eu nunca imaginei que poderia conhecer pessoas tão diferentes e interessantes em um só lugar. A Empiria mudou minha vida!"
        },
        {
            name: "Pedro Henrique",
            text: "Eu sempre fui muito tímido e tinha dificuldade de fazer novas amizades. Mas depois que me cadastrei na Empiria, minha vida social mudou completamente!"
        },
        {
            name: "Isabela Santos",
            text: "Eu amo viajar e conhecer novas culturas. Com a Empiria, eu tenho a oportunidade de fazer isso e ainda conhecer pessoas incríveis!"
        }
    ];

    const nextSlide = () => setActiveCarouselIndex((p) => (p + 1) % encontros.length);
    const prevSlide = () => setActiveCarouselIndex((p) => (p - 1 + encontros.length) % encontros.length);

    return (
        <Box sx={{ width: '100%', bgcolor: 'white', marginTop: '54px' }}>
            <LandingNavbar />

            {/* Hero Section */}
            <Box
                sx={{
                    pt: { xs: 4, md: 8 },
                    pb: { xs: 6, md: 12 },
                    backgroundColor: '#e6f1f1',
                    backgroundImage: `url(${logo_cinza})`,
                    backgroundSize: '620px auto',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    color: 'white',
                    minHeight: { xs: 420, md: 560 },
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        {/* Left Column */}
                        <Grid item xs={12} md={6}>
                            <Reveal as={Box}>
                                <Typography variant={mobile ? "h4" : "h3"} fontWeight="bold" mb={3} lineHeight={1.2} color="black">
                                    A vida é feita de<br />
                                    experiências incríveis,<br />
                                    encontre e compartilhe as suas na Empiria
                                </Typography>
                            </Reveal>

                            <Reveal delay={80}>
                                <Typography variant="h6" color="#006a61" mb={4}>
                                    Encontre pessoas improváveis<br />
                                    e tenha <strong>experiências incríveis!</strong>
                                </Typography>
                            </Reveal>

                            <Reveal delay={160}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/criar-conta')}
                                    startIcon={<KeyboardArrowDown />}
                                    sx={{
                                        bgcolor: '#006a61',
                                        '&:hover': {
                                            bgcolor: '#1E7B54',
                                            transform: 'translateY(-2px)',
                                            boxShadow: 4
                                        },
                                        borderRadius: 2,
                                        px: 4,
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    Cadastre-se gratuitamente
                                </Button>
                            </Reveal>
                        </Grid>

                        {/* Right Column - Video Card */}
                        <Grid item xs={12} md={6}>
                            <Reveal>
                                <Card elevation={0} sx={{ backgroundColor: 'transparent', boxShadow: 'none', backdropFilter: 'none', border: 'none' }}>
                                    <CardContent sx={{ p: 0 }}>
                                        <Typography variant="h5" fontWeight="bold" color="#006a61" textAlign="center" mb={2}>
                                            Conheça a Empiria
                                        </Typography>

                                        <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                                            <Box sx={{ position: 'absolute', inset: 0 }}>
                                                <video
                                                    ref={videoRef}
                                                    src={`${process.env.PUBLIC_URL}/lv_0_20240613110733.mp4`}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                    controls
                                                    playsInline
                                                    preload="metadata"
                                                    onPlay={() => setIsPlaying(true)}
                                                    onPause={() => setIsPlaying(false)}
                                                />
                                            </Box>

                                            {!isPlaying && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        display: { xs: 'none', md: 'flex' },
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        zIndex: 2,
                                                        pointerEvents: 'none',
                                                    }}
                                                >
                                                    <IconButton
                                                        onClick={() => {
                                                            if (videoRef.current) {
                                                                videoRef.current.muted = false;
                                                                videoRef.current.play();
                                                            }
                                                        }}
                                                        sx={{
                                                            pointerEvents: 'auto',
                                                            bgcolor: 'rgba(255,255,255,0.2)',
                                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                                            width: 72,
                                                            height: 72,
                                                        }}
                                                    >
                                                        <PlayArrow sx={{ fontSize: 44, color: 'white' }} />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </Box>

                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Reveal delay={80}>
                                                    <Button
                                                        variant="contained"
                                                        fullWidth
                                                        onClick={scrollToAnfitriao}
                                                        sx={{
                                                            background: 'linear-gradient(135deg, #006a61 0%, #1E7B54 100%)',
                                                            '&:hover': {
                                                                background: 'linear-gradient(135deg, #1E7B54 0%, #006a61 100%)',
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: '0 8px 20px rgba(67, 209, 150, 0.3)'
                                                            },
                                                            py: 1.5,
                                                            textTransform: 'none',
                                                            fontWeight: 'bold',
                                                            transition: 'all 0.3s ease'
                                                        }}
                                                    >
                                                        Anfitrião
                                                    </Button>
                                                </Reveal>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Reveal delay={140}>
                                                    <Button
                                                        fullWidth
                                                        onClick={scrollToConviva}
                                                        sx={{
                                                            background: 'white',
                                                            boxShadow: '0 8px 20px rgba(67, 209, 150, 0.3)',
                                                            '&:hover': {
                                                                background: 'white',
                                                                transform: 'translateY(-2px)',
                                                            },
                                                            py: 1.5,
                                                            color: '#006a61',
                                                            textTransform: 'none',
                                                            fontWeight: 'bold',
                                                            transition: 'all 0.3s ease'
                                                        }}
                                                    >
                                                        Conviva
                                                    </Button>
                                                </Reveal>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Reveal>
                        </Grid>
                    </Grid>

                    {/* Stats Cards */}
                    <Grid container spacing={3} mt={4} alignItems="stretch">
                        {[
                            { icon: <People />, text: "Milhares de encontros realizados" },
                            { icon: <Language />, text: "Conheça pessoas de diferentes áreas" },
                            { icon: <Event />, text: "Disponível em todo tempo" },
                            { icon: <People />, text: "Milhares de pessoas cadastradas" }
                        ].map((stat, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Reveal delay={index * 80} fullHeight>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            textAlign: 'center',
                                            p: 3,
                                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                                            backgroundColor: 'rgba(255,255,255,0.5)',
                                            transition: 'all 0.3s ease',
                                            borderStyle: 'solid',
                                            borderWidth: '1px',
                                            borderColor: '#e2e2e2a4',
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                                                backgroundColor: 'rgba(255,255,255,1)'
                                            }
                                        }}
                                    >
                                        <Box sx={{ color: '#006a61', mb: 2 }}>
                                            {React.cloneElement(stat.icon, { sx: { fontSize: 48 } })}
                                        </Box>
                                        <Typography fontWeight="bold" color="#006a61">
                                            {stat.text}
                                        </Typography>
                                    </Card>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Anfitrião Section */}
            <Box
                ref={anfitriaoRef}
                sx={{
                    position: 'relative',
                    py: { xs: 6, md: 10 },
                    background: 'linear-gradient(135deg, #006a61 0%, #1E7B54 100%)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(45deg, rgba(255,255,255,0.05) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.05) 75%, rgba(255,255,255,0.05))',
                        backgroundSize: '40px 40px',
                        opacity: 0.1,
                        pointerEvents: 'none'
                    }
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Reveal>
                                <Typography variant={mobile ? "h4" : "h3"} fontWeight="bold" color="white" mb={3}>
                                    Anfitrião
                                </Typography>
                            </Reveal>
                            <Reveal delay={80}>
                                <Typography variant="body1" color="white" mb={4} sx={{ lineHeight: 1.8 }}>
                                    O Anfitrião é alguém que deseja compartilhar seu conhecimento adquirido por dedicação e experiência,
                                    inspirando, motivando e ajudando os outros. Profissionais como Psicólogos, Arquitetos, Advogados,
                                    Médicos, Personal Trainers, Fisioterapeutas, Músicos e outros autônomos podem agendar encontros
                                    na sua área e atender clientes interessados nos seus serviços. Qualquer pessoa que tenha algo
                                    relevante e inspirador para compartilhar pode ser um Anfitrião e marcar um encontro.
                                </Typography>
                            </Reveal>
                            <Reveal delay={140}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/criar-conta?sign=encontrado')}
                                    startIcon={<KeyboardArrowDown />}
                                    sx={{
                                        bgcolor: 'white',
                                        color: '#006a61',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.95)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                                        },
                                        borderRadius: 2,
                                        px: 4,
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    Cadastre-se como anfitrião
                                </Button>
                            </Reveal>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Reveal>
                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL}/ALLAI-1.jpg`}
                                    alt="Anfitrião"
                                    sx={{ width: '100%', borderRadius: 4, boxShadow: 4 }}
                                />
                            </Reveal>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Conviva Section */}
            <Box
                ref={convivaRef}
                sx={{
                    position: 'relative',
                    py: { xs: 6, md: 10 },
                    background: 'linear-gradient(135deg, #1E7B54 0%, #006a61 100%)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                        pointerEvents: 'none'
                    }
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                            <Reveal>
                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL}/2.jpg`}
                                    alt="Conviva"
                                    sx={{ width: '100%', borderRadius: 4, boxShadow: 4 }}
                                />
                            </Reveal>
                        </Grid>
                        <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                            <Reveal>
                                <Typography variant={mobile ? "h4" : "h3"} fontWeight="bold" color="white" mb={3}>
                                    Conviva
                                </Typography>
                            </Reveal>
                            <Reveal delay={80}>
                                <Typography variant="body1" color="white" mb={4} sx={{ lineHeight: 1.8 }}>
                                    Conviva é alguém que busca crescimento pessoal através da experiência de um Anfitrião,
                                    procurando serviços, reconhecimento, inspiração ou troca de ideias. Por exemplo, para acessar
                                    profissionais como psicólogos de maneira rápida e segura, basta se cadastrar na plataforma,
                                    escolher o encontro desejado e agendá-lo. Assim, no dia e hora marcados, é só entrar em
                                    videochamada pela plataforma e viver essa grande experiência.
                                </Typography>
                            </Reveal>
                            <Reveal delay={140}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/criar-conta')}
                                    startIcon={<KeyboardArrowDown />}
                                    sx={{
                                        bgcolor: 'white',
                                        color: '#006a61',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.95)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                                        },
                                        borderRadius: 2,
                                        px: 4,
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    Cadastre-se como conviva
                                </Button>
                            </Reveal>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Nossa Missão */}
            <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Reveal>
                                <Typography variant={mobile ? "h4" : "h3"} fontWeight="bold" color="#006a61" mb={3}>
                                    Nossa missão
                                </Typography>
                            </Reveal>
                            <Reveal delay={80}>
                                <Typography variant="body1" color="text.secondary" mb={2} sx={{ lineHeight: 1.8 }}>
                                    Na Empiria, nosso objetivo é promover <strong>encontros extraordinários entre pessoas improváveis.</strong>
                                </Typography>
                            </Reveal>
                            <Reveal delay={140}>
                                <Typography variant="body1" color="text.secondary" mb={4} sx={{ lineHeight: 1.8 }}>
                                    Acreditamos que a vida é muito mais interessante quando conhecemos pessoas diferentes e{' '}
                                    <strong>compartilhamos experiências únicas.</strong>
                                </Typography>
                            </Reveal>
                            <Reveal delay={200}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/criar-conta')}
                                    startIcon={<KeyboardArrowDown />}
                                    sx={{
                                        background: 'linear-gradient(135deg, #006a61 0%, #1E7B54 100%)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #1E7B54 0%, #006a61 100%)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 20px rgba(67, 209, 150, 0.3)'
                                        },
                                        borderRadius: 2,
                                        px: 4,
                                        py: 1.5,
                                        textTransform: 'none',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    Cadastre-se gratuitamente
                                </Button>
                            </Reveal>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Reveal>
                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL}/Design-sem-nome-2024-06-04T102814.170.png`}
                                    alt="Nossa missão"
                                    sx={{ width: '100%', borderRadius: 4, boxShadow: 4 }}
                                />
                            </Reveal>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Por que escolher */}
            <Box sx={{
                position: 'relative',
                py: { xs: 6, md: 10 },
                background: 'linear-gradient(180deg, #006a61 0%, #1E7B54 100%)',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    right: '-10%',
                    width: '500px',
                    height: '500px',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }
            }}>
                <Container maxWidth="lg">
                    <Reveal>
                        <Typography variant={mobile ? "h4" : "h3"} fontWeight="bold" color="white" textAlign="center" mb={6}>
                            Por que escolher a Empiria?
                        </Typography>
                    </Reveal>

                    <Reveal delay={80}>
                        <Box sx={{ mb: 6, textAlign: 'center' }}>
                            <Box
                                component="img"
                                src={`${process.env.PUBLIC_URL}/image-2.png`}
                                alt="Empiria App"
                                sx={{ maxWidth: '75%', height: 'auto', borderRadius: 2 }}
                            />
                        </Box>
                    </Reveal>

                    <Grid container spacing={3} alignItems="stretch">
                        {[
                            {
                                icon: <People />,
                                title: "Encontros surpreendentes",
                                text: "Oferecemos uma variedade de encontros e experiências incríveis que vão te surpreender e fazer você mudar de perspectiva"
                            },
                            {
                                icon: <Security />,
                                title: "Qualidade e segurança",
                                text: "Priorizamos a qualidade e a segurança em todos os encontros realizados na Empiria para que você possa aproveitar ao máximo sem se preocupar com nada"
                            },
                            {
                                icon: <CalendarToday />,
                                title: "Personalizado para você",
                                text: "Nossos encontro são personalizados à escolha de cada participante seja como Anfitrião seja como Conviva, levando em consideração suas preferências e interesses"
                            },
                            {
                                icon: <Headset />,
                                title: "Suporte especializado",
                                text: "Conte com a ajuda de nossos colaboradores para tirar qualquer dúvida sobre nossa plataforma e sobre o que a empiria pode fazer por você"
                            }
                        ].map((feature, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Reveal delay={index * 80} fullHeight>
                                    <Card sx={{
                                        height: '100%',
                                        textAlign: 'center',
                                        p: 3,
                                        bgcolor: 'rgba(255,255,255,0.95)',
                                        backdropFilter: 'blur(10px)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                                            bgcolor: 'white'
                                        }
                                    }}>
                                        <Box sx={{ color: '#006a61', mb: 2 }}>
                                            {React.cloneElement(feature.icon, { sx: { fontSize: 48 } })}
                                        </Box>
                                        <Typography variant="h6" fontWeight="bold" color="#006a61" mb={2}>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {feature.text}
                                        </Typography>
                                    </Card>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Encontros acontecendo agora */}
            <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Reveal>
                        <Typography variant={mobile ? "h4" : "h3"} fontWeight="bold" color="#006a61" textAlign="center" mb={6}>
                            Encontros Acontecendo Agora
                        </Typography>
                    </Reveal>

                    {data && data.length > 0 ? (
                        <SwiperNavContainer>
                            <NavButton className="prev" ref={prevRef}><ArrowBackIosIcon /></NavButton>
                            <NavButton className="next" ref={nextRef}><ArrowForwardIosIcon /></NavButton>
                            <SwiperFadeWrapper>
                                <StyledSwiper
                                    modules={[Navigation, Autoplay]}
                                    spaceBetween={15}
                                    slidesPerView="auto"
                                    loop
                                    autoplay={{
                                        delay: 10000,
                                        disableOnInteraction: false,
                                    }}
                                    slidesOffsetBefore={48}
                                    pagination={{ clickable: true }}
                                    navigation={{
                                        prevEl: prevRef.current,
                                        nextEl: nextRef.current,
                                    }}
                                    onBeforeInit={(swiper) => {
                                        if (typeof swiper.params.navigation !== 'boolean') {
                                            swiper.params.navigation.prevEl = prevRef.current;
                                            swiper.params.navigation.nextEl = nextRef.current;
                                        }
                                    }}
                                >
                                    {data.map((meeting, i) => {
                                        const host = meeting.Profile?.User;
                                        const schedule = meeting.meeting_schedule?.[0];
                                        const conviva = schedule?.Order?.[0]?.user;
                                        const startTime = schedule?.datetime ? new Date(schedule.datetime) : null;
                                        const minutesAgo = startTime ? Math.max(0, Math.floor((Date.now() - startTime.getTime()) / 60000)) : 0;

                                        return (
                                            <SwiperSlide key={meeting.id} style={{ width: "22rem" }}>
                                                <Reveal delay={i * 60} fullHeight>
                                                    <Box
                                                        onClick={() => {
                                                            if (schedule?.id) navigate(`/lista/ao-vivo/${schedule.id}`);
                                                        }}
                                                        sx={{
                                                            bgcolor: 'white',
                                                            borderRadius: '16px',
                                                            border: '1px solid #e8e8e8',
                                                            overflow: 'hidden',
                                                            height: '100%',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                borderColor: '#2e7d32',
                                                                boxShadow: '0 4px 20px rgba(46,125,50,0.12)',
                                                                transform: 'translateY(-2px)',
                                                            },
                                                        }}>
                                                        {/* Barra ao vivo */}
                                                        <Box sx={{ bgcolor: '#2e7d32', px: 2, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                                                <FiberManualRecordIcon sx={{
                                                                    fontSize: 8, color: 'white',
                                                                    animation: 'pulse 1.5s infinite',
                                                                    '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                                                                }} />
                                                                <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, fontSize: '0.7rem', letterSpacing: 0.5 }}>
                                                                    AO VIVO
                                                                </Typography>
                                                            </Stack>
                                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem' }}>
                                                                {minutesAgo > 0 ? `Há ${minutesAgo} min` : 'Agora'}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                            {/* Titulo */}
                                                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, lineHeight: 1.3 }}>
                                                                {meeting.title}
                                                            </Typography>

                                                            {/* Tags de assunto */}
                                                            {(meeting.topic || meeting.subtopic) && (
                                                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                                                                    {meeting.topic && (
                                                                        <Chip label={meeting.topic} size="small" sx={{ bgcolor: '#e6f3f1', color: '#006a61', fontWeight: 500, fontSize: '0.7rem', height: 22 }} />
                                                                    )}
                                                                    {meeting.subtopic && (
                                                                        <Chip label={meeting.subtopic} size="small" variant="outlined" sx={{ borderColor: '#ccc', color: '#666', fontSize: '0.7rem', height: 22 }} />
                                                                    )}
                                                                </Stack>
                                                            )}

                                                            {/* Participantes */}
                                                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{
                                                                bgcolor: '#fafafa', borderRadius: '12px', p: 1.5, mt: 'auto',
                                                            }}>
                                                                <Avatar src={host?.profileImage} sx={{ width: 32, height: 32, border: '2px solid #006a61', flexShrink: 0 }}>
                                                                    {host?.name?.[0]}
                                                                </Avatar>
                                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', lineHeight: 1 }}>
                                                                        Anfitrião
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight={500} noWrap sx={{ fontSize: '0.75rem' }}>
                                                                        {host?.name}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ width: '1px', height: 28, bgcolor: '#e0e0e0', flexShrink: 0 }} />
                                                                <Avatar src={conviva?.profileImage} sx={{ width: 32, height: 32, border: '2px solid #1976d2', flexShrink: 0 }}>
                                                                    {conviva?.name?.[0] || '?'}
                                                                </Avatar>
                                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', lineHeight: 1 }}>
                                                                        Conviva
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight={500} noWrap sx={{ fontSize: '0.75rem' }}>
                                                                        {conviva?.name || 'Aguardando'}
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>

                                                            {/* Botao assistir */}
                                                            <Button
                                                                fullWidth
                                                                variant="contained"
                                                                startIcon={<Visibility />}
                                                                sx={{
                                                                    mt: 2,
                                                                    bgcolor: '#2e7d32',
                                                                    borderRadius: '8px',
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.85rem',
                                                                    height: 40,
                                                                    '&:hover': { bgcolor: '#1b5e20' },
                                                                }}
                                                            >
                                                                Assistir como espectador
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                </Reveal>
                                            </SwiperSlide>
                                        );
                                    })}
                                </StyledSwiper>
                            </SwiperFadeWrapper>
                        </SwiperNavContainer>
                    ) : (
                        <Reveal>
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    py: 8,
                                    px: 3,
                                }}
                            >
                                <Event
                                    sx={{
                                        fontSize: 80,
                                        color: '#006a61',
                                        opacity: 0.3,
                                        mb: 3
                                    }}
                                />
                                <Typography
                                    variant="h5"
                                    color="#006a61"
                                    fontWeight="bold"
                                    mb={2}
                                >
                                    Nenhum encontro disponível no momento
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    mb={4}
                                    sx={{ maxWidth: 600, mx: 'auto' }}
                                >
                                    Novos encontros incríveis estão sendo criados. Cadastre-se agora para ser notificado quando novos encontros estiverem disponíveis!
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/criar-conta')}
                                    sx={{
                                        background: 'linear-gradient(135deg, #006a61 0%, #1E7B54 100%)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #1E7B54 0%, #006a61 100%)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 20px rgba(67, 209, 150, 0.3)'
                                        },
                                        borderRadius: 2,
                                        px: 4,
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    Cadastre-se gratuitamente
                                </Button>
                            </Box>
                        </Reveal>
                    )}
                </Container>
            </Box>

            {/* Testimonials */}
            <Box sx={{
                position: 'relative',
                py: { xs: 6, md: 10 },
                background: 'linear-gradient(180deg, #006a61 0%, #1E7B54 100%)',
                overflow: 'hidden',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-50%',
                    left: '-10%',
                    width: '600px',
                    height: '600px',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }
            }}>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Reveal>
                        <Card sx={{
                            borderRadius: 4,
                            p: { xs: 3, md: 6 },
                            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,1) 100%)'
                        }}>
                            <Typography variant={mobile ? "h5" : "h4"} fontWeight="bold" color="#006a61" textAlign="center" mb={6}>
                                O que as pessoas acharam da nossa plataforma
                            </Typography>

                            <Grid container spacing={4} alignItems="stretch">
                                {testimonials.map((testimonial, index) => (
                                    <Grid item xs={12} md={4} key={index}>
                                        <Reveal delay={index * 80} fullHeight>
                                            <Card sx={{
                                                height: '100%',
                                                textAlign: 'center',
                                                p: 3,
                                                bgcolor: '#F9FAFB',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-8px)',
                                                    boxShadow: '0 12px 30px rgba(67, 209, 150, 0.2)',
                                                    bgcolor: 'white'
                                                }
                                            }}>
                                                <Box
                                                    sx={{
                                                        width: 64,
                                                        height: 64,
                                                        borderRadius: '50%',
                                                        background: 'linear-gradient(135deg, rgba(67, 209, 150, 0.2) 0%, rgba(67, 209, 150, 0.1) 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        mx: 'auto',
                                                        mb: 2
                                                    }}
                                                >
                                                    <Person sx={{ fontSize: 32, color: '#006a61' }} />
                                                </Box>
                                                <Typography variant="h6" fontWeight="bold" color="#006a61" mb={2}>
                                                    {testimonial.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                    "{testimonial.text}"
                                                </Typography>
                                            </Card>
                                        </Reveal>
                                    </Grid>
                                ))}
                            </Grid>
                        </Card>
                    </Reveal>
                </Container>
            </Box>

            {/* CTA Final */}
            <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'white', textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Reveal>
                        <Typography variant={mobile ? "h5" : "h4"} fontWeight="bold" color="#006a61" mb={4}>
                            Fale conosco e descubra o próximo passo.
                        </Typography>
                    </Reveal>
                    <Reveal delay={80}>
                        <Box sx={{
                            height: 2,
                            background: 'linear-gradient(90deg, transparent 0%, #006a61 50%, transparent 100%)',
                            maxWidth: 600,
                            mx: 'auto'
                        }} />
                    </Reveal>
                </Container>
            </Box>

            {/* Footer */}
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <Container>
                    <Reveal>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            © Todos os direitos reservados a Empiria
                        </Typography>
                    </Reveal>
                </Container>
            </Box>

            {/* WhatsApp Button */}

            {/* Floating WhatsApp CTA — clean single-circle version */}
            <Box
                onClick={() => navigate('/chat-bot')}
                aria-label="Atendimento"
                sx={{
                    position: 'fixed',
                    bottom: 32,
                    right: 32,
                    zIndex: 1000,

                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',

                    // start as circle
                    height: 64,
                    borderRadius: 999,
                    backgroundColor: '#25D366',
                    color: 'white',
                    boxShadow: 4,
                    textDecoration: 'none',
                    cursor: 'pointer',

                    pl: 1.5, // padding-left = theme.spacing(2.5)
                    pr: 2,
                    width: 64,
                    overflow: 'hidden',

                    transition:
                        'width 250ms ease, box-shadow 200ms ease, background-color 200ms ease',

                    '&:hover, &:focus-visible': {
                        width: 200, // expands to the left because it's fixed to right
                        backgroundColor: '#20BA5A',
                        boxShadow: 6,
                        outline: 'none',
                    },

                    '@media (max-width:600px)': {
                        width: 64,
                        '&:hover, &:focus-visible': { width: 64 },
                    },
                }}
            >
                {/* Icon (stays on the right side) */}
                <Box
                    aria-hidden
                    sx={{
                        marginLeft: 0, // push to the far right
                        width: 40,
                        height: 40,
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                    }}
                >
                    <svg
                        width="32"
                        height="32"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        style={{ display: 'block' }}
                    >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                        <path d="M12 2C6.48 2 2 6.48 2 12c0 1.82.487 3.526 1.338 4.994L2 22l5.152-1.315A9.936 9.936 0 0012 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm0 18a7.965 7.965 0 01-4.06-1.113l-.291-.173-3.019.77.788-2.944-.19-.302A7.958 7.958 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                    </svg>
                </Box>

                {/* Label (hidden until hover) */}
                <Typography
                    className="wa-label"
                    variant="button"
                    sx={{
                        fontWeight: 700,
                        letterSpacing: 0.2,
                        whiteSpace: 'nowrap',
                        pr: 1,
                        opacity: 0,
                        maxWidth: 0,
                        overflow: 'hidden',
                        transition: 'opacity 160ms ease 80ms, max-width 250ms ease 80ms',
                        marginLeft: '10px',
                    }}
                >
                    Atendimento
                </Typography>

                <style>{`
    [aria-label="Atendimento"]:hover .wa-label,
    [aria-label="Atendimento"]:focus-visible .wa-label {
      opacity: 1;
      max-width: 130px;
    }
  `}</style>
            </Box>



        </Box>
    );
};

export default Welcome;
